"use client";

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { trackGa4Event } from "@/analytics/ga4";
import {
	CheckboxConsentField,
	ConfirmationButton,
	FormContainer,
} from "@/components/form";
import { useCountryConfig } from "@/config/country-context";
import { useRuntimeConfig } from "@/config/runtime-config-context";
import { LogEvent } from "@/datadog/events";
import { sendClientLog } from "@/datadog/utils/send-client-log";
import {
	applyMockFormDefaults,
	getMockLoanCustomerDefaults,
} from "@/dev/console/data/mock-form-values";
import { useConstantSelectOptions } from "@/features/customer-information/hooks/use-constant-options";
import { useCustomerInformationSubmit } from "@/features/customer-information/hooks/use-customer-information-submit";
import { useLoanCustomerForm } from "@/features/customer-information/hooks/use-loan-customer-form";
import { useTrustedAccountFields } from "@/features/customer-information/hooks/use-trusted-account-fields";
import { TaskType } from "@/types";
import {
	LoanCustomerInformationContext,
	LoanCustomerInformationPayload,
} from "../types";
import {
	resolveSubmitPurpose,
	shouldShowLoanPurposeSelectFromContext,
} from "../utils";
import { mapLoanCustomerFormToPayload } from "../utils/loan-customer-payload";
import { CustomerKycFields } from "./fields/customer-kyc-fields";

interface Props {
	taskId: string;
	context: LoanCustomerInformationContext;
}

const LoanCustomerComponent = ({ taskId, context }: Props) => {
	const { flowId } = useParams<{ flowId: string }>();
	const t = useTranslations("pages.CustomerInformationPage");
	const loanPurposeOptions = useConstantSelectOptions("loan-purpose");
	const showLoanPurposeSelect = shouldShowLoanPurposeSelectFromContext(context);
	const { country, rules } = useCountryConfig();
	const { mock } = useRuntimeConfig();

	const { isCoapplicant, accountNumbers } = context;
	const hasMultipleTrustedAccounts =
		rules.payoutAccount === "swedish" && (accountNumbers?.length ?? 0) > 1;

	const trustedAccountFields = useTrustedAccountFields({
		accountNumbers,
		flowId,
		country,
	});
	const { isWholeAccountNumber } = trustedAccountFields;

	const { useForm, askNumberOfCars } = useLoanCustomerForm({ context });
	const methods = useForm;
	const { reset, handleSubmit } = methods;

	const submit = useCustomerInformationSubmit<LoanCustomerInformationPayload>({
		taskId,
		processType: context.process,
	});

	useEffect(() => {
		const { accountNumber, clearingNumber } = trustedAccountFields;

		reset(
			applyMockFormDefaults(
				{
					accountNumber,
					clearingNumber,
					isWholeAccountNumber,
					prefilledAccountNumber: accountNumber,
					citizenship: rules.fixedCitizenship
						? [...rules.fixedCitizenship]
						: (context.citizenships ?? []),
					consent: false,
					loanPurpose: "",
					dueDate: "",
					numberOfCars: null,
				},
				getMockLoanCustomerDefaults(
					country,
					showLoanPurposeSelect,
					askNumberOfCars
				),
				mock
			)
		);
	}, [
		context,
		trustedAccountFields,
		isWholeAccountNumber,
		reset,
		showLoanPurposeSelect,
		askNumberOfCars,
		rules,
		country,
		mock,
	]);

	const onSubmit = handleSubmit(async (values) => {
		const purpose = resolveSubmitPurpose(context, values.loanPurpose);

		if (!purpose) {
			// FLOW rejects a submit without a purpose. Reachable only for a
			// co-applicant whose context carries none, since main applicants are
			// asked for one; log it so the 400 is not a mystery.
			await sendClientLog({
				level: "warn",
				event: LogEvent.FormPurposeMissing,
				message:
					"Submitting customer-information without a purpose: none in context and the customer was not asked",
				task: TaskType.CustomerInformation,
				flowId,
				context: {
					isCoapplicant,
					process: context.process,
					purpose: context.purpose,
				},
			});
		}

		await submit(
			mapLoanCustomerFormToPayload({
				values,
				isCoapplicant,
				purpose,
				country,
				hasMultipleTrustedAccounts,
				mock,
			}),
			() => trackGa4Event("payout_details_submitted", { product_type: "loan" })
		);
	});

	const pepOptions = [
		{ value: true, label: t("binaryResponse.affermativeOptionText") },
		{ value: false, label: t("binaryResponse.opposingOptionText") },
	];

	return (
		<FormContainer
			methods={methods}
			onSubmit={onSubmit}
			getPayloadPreview={(values) =>
				mapLoanCustomerFormToPayload({
					values,
					isCoapplicant,
					purpose: resolveSubmitPurpose(context, values.loanPurpose),
					country,
					hasMultipleTrustedAccounts,
					mock,
				})
			}
		>
			<CustomerKycFields
				accountNumbers={context.accountNumbers}
				prefilledAccountNumber={trustedAccountFields.accountNumber}
				isCoapplicant={isCoapplicant}
				hasMultipleTrustedAccounts={hasMultipleTrustedAccounts}
				isWholeAccountNumber={isWholeAccountNumber}
				showLoanPurposeSelect={showLoanPurposeSelect}
				loanPurposeOptions={loanPurposeOptions}
				askNumberOfCars={askNumberOfCars}
				pepOptions={pepOptions}
			/>

			{rules.autogiroConsent && !isCoapplicant && (
				<CheckboxConsentField name="consent" label={t("checkboxTaC")} />
			)}

			<ConfirmationButton label={t("button.validText")} type="submit" />
		</FormContainer>
	);
};

export default LoanCustomerComponent;
